/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { ColorName, Colors } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { COLOR_KEYS, MEDIA_MIN_WIDTH } from '../../../constants';
import { SectionName, useAvroTracking } from '../../../observability';
import { CustomBrandButton } from '../../../components/CustomBrandButton';

type CTARoundedProps = {
  children: React.ReactNode;
  ctaText?: string;
  ctaHref?: string;
  heading: string;
  backgroundColor: ColorName;
  secondaryBackgroundColor?: ColorName;
};

const Styles = labelStyles('CTARounded', {
  root: (backgroundColor: ColorName) =>
    css({
      backgroundColor: backgroundColor ? Colors[backgroundColor] : 'inherit',
    }),
  content: (backgroundColor: ColorName) =>
    css({
      backgroundColor: Colors[backgroundColor],
      borderRadius: '60px 60px 0 0',
      paddingBottom: '32px',
      paddingTop: '48px',
      [MEDIA_MIN_WIDTH.small]: {
        borderRadius: '120px 120px 0 0',
        paddingTop: '70px',
        paddingBottom: '49px',
      },
      [MEDIA_MIN_WIDTH.large]: {
        paddingTop: '96px',
        paddingBottom: '92px',
      },
    }),
  headingWrapper: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: '16px',
    marginBottom: '32px',
    [MEDIA_MIN_WIDTH.small]: {
      rowGap: '52px',
      marginBottom: '59px',
    },
    [MEDIA_MIN_WIDTH.large]: {
      rowGap: '32px',
    },
  }),
  heading: css({
    maxWidth: '315px',
    fontFamily: 'GT Alpina',
    fontStyle: 'normal',
    fontWeight: 250,
    fontSize: '36px',
    textAlign: 'center',
    letterSpacing: '-2px',
    color: Colors.earth,
    lineHeight: '110%',
    [MEDIA_MIN_WIDTH.small]: {
      fontSize: '50px',
      lineHeight: '55px',
      letterSpacing: '-1.5px',
      maxWidth: '510px',
    },
    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '74px',
      lineHeight: '81.4px',
      letterSpacing: '-2.5px',
      maxWidth: '704px',
    },
  }),
  button: css({
    '> a': {
      borderRadius: '40px',
      minWidth: '167px',
      [MEDIA_MIN_WIDTH.small]: {
        minWidth: '182px',
      },
    },
  }),
});

export function CTARounded(props: CTARoundedProps) {
  const { emitCTAClickEvent } = useAvroTracking();
  return (
    <div css={Styles.root(props.secondaryBackgroundColor)}>
      <div css={Styles.content(props.backgroundColor || COLOR_KEYS.sky)}>
        <div css={Styles.headingWrapper}>
          <h2 css={Styles.heading}>{props.heading}</h2>
          {props.ctaHref && props.ctaText && (
            <div css={Styles.button}>
              <CustomBrandButton
                onClick={() =>
                  emitCTAClickEvent({
                    text: props.ctaText || '',
                    href: props.ctaHref || '',
                    section: SectionName.CTARounded,
                  })
                }
                href={props.ctaHref}
              >
                {props.ctaText}
              </CustomBrandButton>
            </div>
          )}
        </div>
        {props.children}
      </div>
    </div>
  );
}
